import { useEffect, useState } from "react";
import { ArrowDownUp, Check, ChevronDown, Loader2, Plus, Save, Target, Trash2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CoachListSection from "./CoachListSection";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import LoadFailedCard from "./LoadFailedCard";
import { FailureCard, SavedCard } from "./GoalNotice";
import { GOAL_STATUSES, GOAL_STATUS_OPTIONS_DRAWN, goalStatusColor, goalStatusLabel, normalizeGoalStatus } from "../lib/goalStatus";
import { GOAL_CATEGORY_LABELS } from "../lib/goalCategory";
import { GOAL_SORTS, sortRows } from "../lib/coachListSort";

/**
 * Issue #460 — the Coach Portal's Goals tab, lifted out of
 * `CoachPortal.jsx` whole.
 *
 * WHY IT MOVED: that page's component function measured 833 lines against
 * the 800-line `max-lines-per-function` budget in `eslint.config.mjs`
 * before this slice added anything. The budget's own header says never to
 * raise a number to make new code fit, so the four list panels come out
 * here instead. Nothing about the goal editing behaviour changed in the
 * move — every testid, string and handler is the one that was there.
 *
 * WHAT IS NEW: the sort control and the scroll box, both from
 * `CoachListSection`. This list was the only one of the four with no
 * scroll box at all, so an unbounded goal list pushed the composer and
 * everything under it off the screen.
 */
export default function CoachGoalsPanel(props) {
  // #1221 slice 2: the coach's player page passes `appearance="r5"` and gets the
  // kit version below. Unset (the flag-off page, admin) is the shipped panel.
  if (props.appearance === "r5") return <CoachGoalsPanelR5 {...props} />;
  return <ShippedCoachGoalsPanel {...props} />;
}

function ShippedCoachGoalsPanel({
  goals,
  sort,
  onSortChange,
  editingGoal,
  onBeginEdit,
  onEditingGoalChange,
  onSaveProgress,
  onCancelEdit,
  onDelete,
  onNewGoal,
  touchTargets = false,
}) {
  const rows = sortRows(GOAL_SORTS, sort, goals);

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <CoachListSection
          title="Development goals"
          sorts={GOAL_SORTS}
          sortValue={sort}
          onSortChange={onSortChange}
          sortTestId="cp-goals-sort"
          listTestId="portal-goals"
          controls={
            <Button size="sm" variant="outline" className={touchTargets ? "min-h-[44px]" : undefined} onClick={onNewGoal} data-testid="cp-new-goal-btn">
              <Plus size={14} className="mr-1" /> New goal
            </Button>
          }
        >
          {rows.length === 0 && <p className="text-sm text-slate-500">No goals yet — assign the first one.</p>}
          {rows.map((g) => {
            const isEditing = editingGoal?.goal_id === g.goal_id;
            return (
              <div key={g.goal_id} className="p-4 rounded-lg border border-slate-200">
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900" data-testid={`cp-goal-title-${g.goal_id}`}>{g.goal_title}</div>
                    {g.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{g.description}</div>}
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      {g.category && <Badge variant="outline" className="text-[10px] capitalize">{g.category}</Badge>}
                      <Badge variant="outline" className="text-[10px] capitalize">{g.horizon || "short"}-term</Badge>
                      {g.target_date && <Badge variant="outline" className="text-[10px]">Due {g.target_date}</Badge>}
                      <Badge className="text-[10px]" style={{ background: goalStatusColor(g.status) + "20", color: goalStatusColor(g.status) }}>{goalStatusLabel(g.status)}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!isEditing && (
                      <Button size="sm" variant="ghost" onClick={() => onBeginEdit(g)} data-testid={`cp-goal-edit-${g.goal_id}`}>
                        <Target size={13} className="mr-1" /> Update
                      </Button>
                    )}
                    <button onClick={() => onDelete(g)} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`cp-goal-del-${g.goal_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <div className="mt-2 space-y-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-slate-600 w-16">Progress</span>
                      <input type="range" min="0" max="100" step="5"
                        value={editingGoal.progress_percentage}
                        onChange={(e) => onEditingGoalChange({ ...editingGoal, progress_percentage: Number(e.target.value) })}
                        className="flex-1 accent-[#0B2E59]"
                        data-testid={`cp-goal-progress-slider-${g.goal_id}`} />
                      <span className="text-sm font-mono font-bold w-12 text-right text-[#0B2E59]">{editingGoal.progress_percentage}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-slate-600 w-16">Status</span>
                      <Select value={editingGoal.status} onValueChange={(v) => onEditingGoalChange({ ...editingGoal, status: v })}>
                        <SelectTrigger className="h-8 flex-1" data-testid={`cp-goal-status-${g.goal_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{GOAL_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={onCancelEdit}>Cancel</Button>
                      <Button size="sm" onClick={onSaveProgress} data-testid={`cp-goal-save-${g.goal_id}`} style={{ background: "#0B2E59" }}>
                        <Save size={13} className="mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-1 text-xs">
                      <span className="text-slate-500">Progress</span>
                      <span className="font-mono font-semibold text-[#0B2E59]">{g.progress_percentage ?? 0}%</span>
                    </div>
                    <Progress value={g.progress_percentage ?? 0} className="h-2" />
                  </div>
                )}
              </div>
            );
          })}
        </CoachListSection>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// #1221 slice 2 (D240, D251) — `CoachPlayerGoals.dc.html`'s Goals body, in the kit.
//
// "Development goals" with New goal and a sort; each goal a card with its
// category, horizon, due date and status, its progress, an Update control and a
// delete control that are ON EVERY CARD (a phone has no hover); Update opens the
// inline Progress/Status editor in the card. Loading, empty and failed are three
// different nodes (D53 ruling 2): a list that has not arrived is not an empty
// list. A failed update shows on the card, not as a toast (D57), and deleting
// asks in the page (D223.1, `ConfirmDeleteDialog`), never through the browser's native prompt.
//
// The writes are the page's: `onSaveProgress()` and `onRemove(goal)` return a
// promise that rejects on failure; this panel shows the failure where it
// happened. No `Card`, `Button` or `Select` from `ui/` is rendered here.
// ---------------------------------------------------------------------------

const R5_FRAME = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 14,
  minWidth: 0,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
};

const BOX_BTN = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  minHeight: 44,
  padding: "0 14px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line-strong)",
  borderRadius: "var(--nw-radius-control)",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--nw-navy)",
  cursor: "pointer",
};

const CHIP = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nw-navy)",
  background: "var(--nw-navy-tint)",
  padding: "2px 8px",
  borderRadius: "var(--nw-radius-pill)",
  whiteSpace: "nowrap",
};

const ROW_LABEL = { width: 64, flexShrink: 0, fontSize: 12, fontWeight: 600, color: "var(--nw-ink-lede)" };

const SORT_BOX = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  minHeight: 44,
  background: "var(--nw-surface)",
  border: "1px solid",
  borderColor: "var(--nw-line)",
  borderRadius: "var(--nw-radius-control)",
};

const SORT_GLYPH = { position: "absolute", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" };

const SORT_STYLE = {
  appearance: "none",
  WebkitAppearance: "none",
  minHeight: 44,
  padding: "0 30px 0 32px",
  background: "transparent",
  border: 0,
  borderRadius: "var(--nw-radius-control)",
  fontSize: 13,
  color: "var(--nw-ink)",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  cursor: "pointer",
};

const categoryLabel = (c) => GOAL_CATEGORY_LABELS[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "");
const statusLabel = (status) => GOAL_STATUS_OPTIONS_DRAWN.find((o) => o.value === normalizeGoalStatus(status))?.label || "Not started";

function GoalCardR5({ g, isEditing, editingGoal, onBeginEdit, onEditingGoalChange, onSaveProgress, onCancelEdit, onRequestDelete }) {
  const id = g.goal_id;
  const progress = g.progress_percentage ?? 0;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  // A different goal's edit, or none: the old failure is not this goal's.
  useEffect(() => {
    if (!isEditing) setSaveError(false);
  }, [isEditing]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(false);
    try {
      await onSaveProgress();
    } catch {
      setSaveError(true);
    }
    setSaving(false);
  };

  return (
    <div data-testid={`cp-goal-card-${id}`} style={{ ...R5_FRAME, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexGrow: 1, minWidth: 0 }}>
          <div
            data-testid={`cp-goal-title-${id}`}
            style={{ fontWeight: 700, fontSize: 15, color: "var(--nw-ink)", overflowWrap: "anywhere" }}
          >
            {g.goal_title}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            {g.category && <span style={CHIP}>{categoryLabel(g.category)}</span>}
            <span style={CHIP}>{g.horizon === "long" ? "Long-term" : "Short-term"}</span>
            {g.target_date && <span style={CHIP}>Due {g.target_date}</span>}
            <span style={CHIP}>{statusLabel(g.status)}</span>
          </div>
        </div>
        <button
          type="button"
          aria-label="Delete goal"
          onClick={() => onRequestDelete(g)}
          data-testid={`cp-goal-del-${id}`}
          style={{ width: 44, height: 44, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--nw-radius-control)", border: "1px solid var(--nw-line)", background: "var(--nw-surface)", cursor: "pointer" }}
        >
          <Trash2 size={16} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {isEditing ? (
        <div
          style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label htmlFor={`cp-goal-progress-${id}`} style={ROW_LABEL}>
              Progress
            </label>
            <input
              id={`cp-goal-progress-${id}`}
              type="range"
              min="0"
              max="100"
              step="5"
              value={editingGoal.progress_percentage}
              disabled={saving}
              onChange={(e) => onEditingGoalChange({ ...editingGoal, progress_percentage: Number(e.target.value) })}
              className="nw-range"
              style={{ flexGrow: 1, minWidth: 0, "--nw-fill": `${editingGoal.progress_percentage}%` }}
              data-testid={`cp-goal-progress-slider-${id}`}
            />
            <span style={{ width: 40, textAlign: "right", fontSize: 14, fontWeight: 700, color: "var(--nw-navy)" }}>{editingGoal.progress_percentage}%</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label htmlFor={`cp-goal-status-${id}`} style={ROW_LABEL}>
              Status
            </label>
            <select
              id={`cp-goal-status-${id}`}
              value={editingGoal.status}
              disabled={saving}
              onChange={(e) => onEditingGoalChange({ ...editingGoal, status: e.target.value })}
              data-testid={`cp-goal-status-${id}`}
              style={{ flexGrow: 1, minWidth: 0, minHeight: 44, padding: "0 12px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", fontSize: 15, color: "var(--nw-ink)", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}
            >
              {GOAL_STATUS_OPTIONS_DRAWN.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {saveError && <FailureCard data-testid="cp-goal-progress-error" title="Failed to update progress" />}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16 }}>
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={saving}
              data-testid={`cp-goal-cancel-${id}`}
              style={{ display: "inline-flex", alignItems: "center", minHeight: 44, minWidth: 44, background: "none", border: "none", padding: 0, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`cp-goal-save-${id}`}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 44, padding: "0 16px", background: "var(--nw-navy)", color: "#FFFFFF", border: "none", fontWeight: 600, fontSize: 14, borderRadius: "var(--nw-radius-control)", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Check size={16} strokeWidth={2} aria-hidden="true" />}
              Save
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>Progress</span>
            <span data-testid={`cp-goal-progress-${id}`} style={{ fontSize: 13, fontWeight: 700, color: "var(--nw-navy)" }}>
              {progress}%
            </span>
          </div>
          <div aria-hidden="true" style={{ height: 6, borderRadius: "var(--nw-radius-pill)", background: "var(--nw-line)" }}>
            <div style={{ width: `${Math.max(0, Math.min(100, Number(progress) || 0))}%`, height: 6, borderRadius: "var(--nw-radius-pill)", background: "var(--nw-navy)" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 8 }}>
            <button type="button" style={BOX_BTN} onClick={() => onBeginEdit(g)} data-testid={`cp-goal-edit-${id}`}>
              <Target size={15} strokeWidth={2} aria-hidden="true" />
              Update
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GoalsLoading() {
  const bar = { height: 14, borderRadius: 6, background: "var(--nw-line)" };
  return (
    <div
      data-testid="cp-goals-loading"
      aria-busy="true"
      style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16, background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
    >
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div style={{ ...bar, width: "55%" }} />
          <div style={{ ...bar, width: "22%" }} />
        </div>
      ))}
    </div>
  );
}

function CoachGoalsPanelR5({
  goals,
  status = "loaded",
  sort,
  onSortChange,
  editingGoal,
  onBeginEdit,
  onEditingGoalChange,
  onSaveProgress,
  onCancelEdit,
  onRemove,
  onNewGoal,
  onRetry,
  notice = null,
}) {
  const rows = sortRows(GOAL_SORTS, sort, goals);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onRemove(deleteTarget);
      setDeleteTarget(null);
    } catch {
      setDeleteError("Nothing has been lost — we just couldn't reach the server.");
    }
    setDeleting(false);
  };

  const heading = (
    <h3
      tabIndex={-1}
      data-focus-after-delete
      style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 17, color: "var(--nw-ink)", outline: "none" }}
    >
      Development goals
    </h3>
  );

  if (status === "loading") {
    return (
      <div style={R5_FRAME}>
        <div aria-hidden="true" style={{ height: 14, width: "40%", borderRadius: 6, background: "var(--nw-line)" }} />
        <GoalsLoading />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div style={R5_FRAME}>
        {heading}
        <LoadFailedCard title="Couldn't load this player's goals" onRetry={onRetry} testId="cp-goals-load-failed" />
      </div>
    );
  }

  return (
    <div style={R5_FRAME}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {heading}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button type="button" style={BOX_BTN} onClick={onNewGoal} data-testid="cp-new-goal-btn">
            <Plus size={15} strokeWidth={2} aria-hidden="true" />
            New goal
          </button>
          {/* One bordered box, as drawn: the icon, the current order and a chevron.
              The select is native and fills the box, so the whole box is the tap target;
              the two glyphs sit over it and take no taps. */}
          <span data-testid="cp-goals-sort-box" style={SORT_BOX}>
            <ArrowDownUp size={14} color="var(--nw-ink-muted)" strokeWidth={2} aria-hidden="true" style={{ ...SORT_GLYPH, left: 10 }} />
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value)}
              aria-label="Sort Development goals"
              data-testid="cp-goals-sort"
              style={SORT_STYLE}
            >
              {GOAL_SORTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} color="var(--nw-ink-muted)" strokeWidth={2} aria-hidden="true" style={{ ...SORT_GLYPH, right: 10 }} />
          </span>
        </div>
      </div>

      {notice && <SavedCard data-testid="cp-goal-assigned">{notice}</SavedCard>}

      {rows.length === 0 ? (
        <div
          data-testid="portal-goals"
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "20px 16px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)", textAlign: "center" }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 14, background: "var(--nw-navy-tint)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Target size={20} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" />
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--nw-ink)" }}>No goals yet — assign the first one.</div>
        </div>
      ) : (
        <div data-testid="portal-goals" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((g) => (
            <GoalCardR5
              key={g.goal_id}
              g={g}
              isEditing={editingGoal?.goal_id === g.goal_id}
              editingGoal={editingGoal}
              onBeginEdit={onBeginEdit}
              onEditingGoalChange={onEditingGoalChange}
              onSaveProgress={onSaveProgress}
              onCancelEdit={onCancelEdit}
              onRequestDelete={(goal) => {
                setDeleteError(null);
                setDeleteTarget(goal);
              }}
            />
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.goal_title ?? ""}"?`}
        confirmLabel="Delete goal"
        testId="cp-goal-delete"
        deleting={deleting}
        error={deleteError}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
